import { usePlaceInfo } from '@shared/core/places/usePlaceInfo';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useState, useEffect } from 'react';
import { useTranslation } from '@shared/core';

// Given any date (in milliseconds), make it 4pm
function setFourPm(milliseconds: number): number;
function setFourPm(milliseconds: number | undefined): number | undefined;
function setFourPm(milliseconds: number | undefined): number | undefined {
  if (!milliseconds) return undefined;
  const date = new Date(milliseconds);
  date.setHours(16, 0, 0, 0);
  return date.getTime();
}

export type PrimaryEventLocation = {
  Venue: string;
  Address: string;
  City: string;
  CityState: string;
  CityST: string;
  utcMilliseconds: number;
  timezone: string | undefined;
};

// Precedence (higher on top):
// VENUE/NAME   LOCATION     DATE       TIME
// VenueInfo    VenueInfo
//              Location     Finalized  4pm      <- Location is not guaranteed
// Placeholder  Placeholder  Today      4pm
export function usePrimaryEventLocation(): { location: PrimaryEventLocation; loading: boolean } {
  const { tq } = useTranslation('stationery');
  const [location, setLocation] = useState<PrimaryEventLocation>({
    Venue: tq`Your Venue`,
    Address: '8 Joy Lane',
    City: 'Seattle',
    CityState: 'Seattle, Washington',
    CityST: 'Seattle, WA',
    utcMilliseconds: setFourPm(new Date().getTime()),
    timezone: undefined,
  });
  const { eventInfo, loading: eventLoading } = useEventInfo();
  const { venueInfo, loading: venueLoading } = useEventPlaces();
  const loading = eventLoading || venueLoading;

  const placeId = loading ? null : (venueInfo.googlePlaceId ?? eventInfo?.locationInfo?.placeId);
  const { result: place, isLoading: gettingPlaceInfo, isCalled } = usePlaceInfo('essentials', 'primaryEventLocation', placeId);

  useEffect(() => {
    if (!place) return;

    const addr = (type: string) => place.addressComponents.find(c => c.types.includes(type)) ?? { longText: '', shortText: '' };
    const Venue = venueInfo.name ?? tq`Your Venue`;
    // prettier-ignore
    const [Address, locality] = addr('route').longText
      ? (place.formattedAddress ?? '').split(', ')
      : [ // ex: ['King County', 'Seattle, WA']
        addr('administrative_area_level_2').longText,
        `${addr('locality').longText}, ${addr('administrative_area_level_1').shortText}`
      ];
    const country = addr('country').shortText;
    const City = addr('locality').longText;
    const state = addr('administrative_area_level_1');
    const CityState = country === 'US' ? `${City}, ${state.longText}` : (locality ?? '');
    const CityST = country === 'US' ? `${City}, ${state.shortText}` : (locality ?? '');

    // prettier-ignore
    const utcMilliseconds =
      setFourPm(eventInfo?.finalizedDate?.milliseconds ?? undefined)
      ?? setFourPm(new Date().getTime());
    const timezone = eventInfo?.finalizedDate?.timezone;

    setLocation({ Venue, Address, City, CityState, CityST, utcMilliseconds, timezone });
  }, [place, eventInfo, venueInfo, tq]);

  // When no placeId exists (public gallery case), we're done loading once other data is loaded
  const isPlaceInfoLoading = placeId ? gettingPlaceInfo || !isCalled : false;

  return { location, loading: loading || isPlaceInfoLoading };
}
